import React from 'react';
import SlideLayout, { SlideTitle, SlideContent } from '../components/SlideLayout';
import { ArrowRight, CheckCircle, XCircle, Infinity } from 'lucide-react';

const RLVRSlide = () => {
  return (
    <SlideLayout maxWidth="6xl">
      <SlideTitle 
        subtitle="The paradigm shift from human feedback to verifiable rewards"
      >
        RLVR: Reinforcement Learning with Verifiable Rewards
      </SlideTitle>
      
      <SlideContent>
        {/* Comparison */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* RLHF */}
          <div className="slide-card border-muted/50">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-3 h-3 rounded-full bg-muted-foreground" />
              <h3 className="font-heading text-lg font-semibold text-foreground">RLHF (Traditional)</h3>
            </div>
            
            <div className="space-y-3 text-sm">
              <div className="flex items-start gap-2">
                <span className="text-muted-foreground">Problem:</span>
                <span className="text-foreground">"Write a helpful email"</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-muted-foreground">Reward:</span>
                <span className="text-chart-4">Human preference (subjective)</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-muted-foreground">Scale:</span>
                <span className="text-foreground">~10K examples</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-muted-foreground">Result:</span>
                <span className="text-foreground">Aligned, but not necessarily reasoning</span>
              </div>
            </div>
            
            <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
              <XCircle className="w-4 h-4 text-destructive" />
              Limited by human annotation cost
            </div>
          </div>
          
          {/* RLVR */}
          <div className="slide-card border-accent/50">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-3 h-3 rounded-full bg-accent" />
              <h3 className="font-heading text-lg font-semibold text-foreground">RLVR (New)</h3>
            </div>
            
            <div className="space-y-3 text-sm">
              <div className="flex items-start gap-2">
                <span className="text-muted-foreground">Problem:</span>
                <span className="text-foreground">"Solve: x + 5 = 12"</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-muted-foreground">Reward:</span>
                <span className="text-accent">Correctness check (objective)</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-muted-foreground">Scale:</span>
                <span className="text-foreground">Millions (automatic)</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-muted-foreground">Result:</span>
                <span className="text-accent">Reasoning capabilities emerge</span>
              </div>
            </div>
            
            <div className="mt-4 flex items-center gap-2 text-xs text-accent">
              <CheckCircle className="w-4 h-4" />
              Unlimited synthetic data generation
            </div>
          </div>
        </div>
        
        {/* Why RLVR works */}
        <div className="slide-card">
          <h3 className="font-heading text-lg font-semibold text-foreground mb-4">Why RLVR Works for Reasoning</h3>
          
          <div className="grid md:grid-cols-4 gap-4">
            {[
              { icon: '1', title: 'Verifiable Domains', desc: 'Math, code, logic have objective answers' },
              { icon: '2', title: 'Automatic Reward', desc: 'No human labeling needed' },
              { icon: '3', title: 'Exploration', desc: 'Model learns to "think" through problems' },
              { icon: '4', title: 'CoT Emerges', desc: 'Intermediate steps improve reward' },
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-10 h-10 rounded-full bg-primary/20 border border-primary/30 flex items-center justify-center mx-auto mb-3">
                  <span className="text-primary font-mono font-bold">{item.icon}</span>
                </div>
                <p className="font-heading font-semibold text-foreground text-sm">{item.title}</p>
                <p className="text-xs text-muted-foreground mt-1">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
        
        {/* Network troubleshooting as verifiable */}
        <div className="mt-6 p-4 rounded-xl bg-accent/10 border border-accent/20">
          <p className="text-sm">
            <strong className="text-accent">Network Troubleshooting as Verifiable Task:</strong>
            <span className="text-muted-foreground"> Diagnosis can be verified by executing fixes and checking if the network recovers. This enables RLVR training for NOC automation.</span>
          </p>
        </div>
      </SlideContent>
    </SlideLayout>
  );
};

export default RLVRSlide;
